import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import Switch from "react-switch";
import TableContainer from "../../../components/Common/DataTableContainer";

const TipoMaterial = () => {
    document.title = "Tipo de Material | GraphicForce - Printesco";

    const [tipos, setTipos] = useState([]);
    const [tiposInactivos, setTiposInactivos] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [form, setForm] = useState({ nombre: "" });
    const [saving, setSaving] = useState(false);
    const [formActivo, setFormActivo] = useState(true);

    // For delete confirmation
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [tipoToDelete, setTipoToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [modalError, setModalError] = useState("");
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [tipoToRestore, setTipoToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isPagination] = useState(true);
    const [isCustomPageSize] = useState(true);

    // Fetch todos los tipos de material y separarlos por estado
    const fetchTipos = async () => {
        try {
            // Obtener todos los tipos de material de una vez
            const resultList = await pb.collection("tipo_material").getList(1, 1000, {
                sort: "-created"
            });
            
            // Separar tipos activos e inactivos
            const activos = resultList.items.filter(tipo => tipo.activo !== false);
            const inactivos = resultList.items.filter(tipo => tipo.activo === false);
            
            // Aplicar paginación solo a los activos
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activosPaginados = activos.slice(startIndex, endIndex);
            
            setTipos(activosPaginados);
            setTiposInactivos(inactivos);
            setTotalRecords(activos.length);
        } catch (err) {
            console.error("Error fetching tipos de material:", err);
        }
    };

    useEffect(() => {
        fetchTipos();
    }, [pageIndex, pageSize]);

    const reloadTipos = () => {
        fetchTipos();
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "" });
        setFormActivo(true);
        setModalError("");
        setModalAdd(true);
    };



    const handleRemove = (tipo) => {
        setTipoToDelete(tipo);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!tipoToDelete) return;
        setDeleting(true);
        try {
            const updatedTipo = await pb.collection("tipo_material").update(tipoToDelete.id, { activo: false });
            setTipos(tipos.map(t => (t.id === tipoToDelete.id ? updatedTipo : t)));
            setShowDeleteAlert(false);
            setTipoToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar tipo de material");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setTipoToDelete(null);
    };

    // Show alert and store tipo to restore
    const handleRestore = (tipo) => {
        setTipoToRestore(tipo);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    // Confirm restore: set activo to true
    const confirmRestore = async () => {
        if (!tipoToRestore) return;
        setRestoring(true);
        try {
            await pb.collection("tipo_material").update(tipoToRestore.id, { activo: true });
            fetchTipos();
            setShowRestoreAlert(false);
            setTipoToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar tipo de material");
        }
        setRestoring(false);
    };

    // Cancel restore
    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setTipoToRestore(null);
    };

    const handleChange = e => {
        setForm({ ...form, [e.target.name]: e.target.name === "nombre" ? e.target.value.toUpperCase() : e.target.value });
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            const data = {
                nombre: form.nombre,
                activo: true,
            };
            const newTipo = await pb.collection("tipo_material").create(data);
            setTipos([...tipos, newTipo]);
            setModalAdd(false);
        } catch (err) {
            // Manejo de error de unicidad
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `El tipo de material con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al agregar tipo de material");
            }
        }
        setSaving(false);
    };



    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const tipo = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="danger" size="sm" onClick={() => handleRemove(tipo)} disabled={tipo.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [tipos]);

    // Columnas para tipos de material inactivos
    const columnsInactivos = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const tipo = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(tipo)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [tiposInactivos]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Materiales" breadcrumbItem="Tipo de Material" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && tipoToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará el tipo de material <b>{tipoToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && tipoToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará el tipo de material <b>{tipoToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Tipo de Material
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadTipos}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={tipos}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Tabla de Tipos de Material Inactivos */}
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Tipos de Material Inactivos
                                        <div>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                title="Recargar tabla"
                                                onClick={reloadTipos}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columnsInactivos}
                                                data={tiposInactivos}
                                                isGlobalFilter={false}
                                                isPagination={false}
                                                isCustomPageSize={false}
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Modal Agregar */}
            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Tipo de Material</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>


        </React.Fragment>
    );
};

export default TipoMaterial;